import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin, message, Modal } from 'antd';
import api from '../services/api';
import PageSkeleton from '../components/Skeleton';

const GOLD = '#B8892A';
const INK = '#18140C';
const MUTED = '#685840';
const BG = '#FAF8F5';
const BD = '#E8E0D0';
const GREEN = '#2A6645';
const RED = '#8C1F1F';

const TIER_ICONS = { normal: '🎫', vip: '💎', institution: '👑' };
const TIER_COLORS = { normal: '#A0A0A0', vip: '#3B82F6', institution: GOLD };
const TIER_BG = {
  normal: 'linear-gradient(135deg, #4A4A4A, #2A2A2A)',
  vip: 'linear-gradient(135deg, #1E3A5F, #0F1E3A)',
  institution: 'linear-gradient(135deg, #3D2E0A, #1A1400)',
};

export default function BuyerMembership() {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [myMembership, setMyMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/membership-tiers/public').then(r => r.data),
      api.get('/membership-tiers/my').then(r => r.data).catch(() => null),
    ]).then(([t, m]) => {
      setTiers(t);
      setMyMembership(m);
    }).finally(() => setLoading(false));
  }, []);

  const handlePurchase = (tierCode, tierName, price) => {
    const currentCode = myMembership?.tier_code || 'normal';
    const isUpgrade = currentCode !== 'normal' && currentCode !== tierCode;
    Modal.confirm({
      title: isUpgrade ? `升级至${tierName}` : `购买${tierName}`,
      content: (
        <div style={{ fontSize: 14, lineHeight: 2, color: INK }}>
          <div>会员价格：<b style={{ color: GOLD }}>¥{price}</b></div>
          {isUpgrade && <div style={{ color: GREEN }}>原会员剩余费用将按比例抵扣</div>}
          <div style={{ marginTop: 8, fontSize: 12, color: MUTED }}>将从您的保证金账户扣款</div>
        </div>
      ),
      okText: '确认购买',
      cancelText: '取消',
      onOk: async () => {
        setPurchasing(true);
        try {
          const res = await api.post('/membership-tiers/purchase', { tier_code: tierCode });
          message.success(`${tierName}开通成功！`);
          if (res.data.upgrade_credit > 0) {
            message.info(`已抵扣 ¥${res.data.upgrade_credit.toFixed(2)}`);
          }
          // 刷新状态
          const m = await api.get('/membership-tiers/my');
          setMyMembership(m.data);
        } catch (err) {
          message.error(err.response?.data?.error || '购买失败');
        } finally {
          setPurchasing(false);
        }
      },
    });
  };

  if (loading) return <PageSkeleton />;

  const currentCode = myMembership?.tier_code || 'normal';
  const isExpired = myMembership?.is_expired;
  const tierOrder = { normal: 0, vip: 1, institution: 2 };

  return (
    <div style={{ background: BG, minHeight: '100vh', paddingBottom: 40 }}>
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', padding: '14px 16px',
          background: '#fff', borderBottom: `1px solid ${BD}`,
        }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: INK }}>←</button>
          <span style={{ flex: 1, textAlign: 'center', fontWeight: 600, fontSize: 16, color: INK }}>会员中心</span>
          <span style={{ width: 18 }} />
        </div>

        {/* Current membership card */}
        <div style={{
          margin: '16px 16px 0', borderRadius: 14, overflow: 'hidden',
          background: TIER_BG[currentCode] || TIER_BG.normal,
          padding: '24px 24px 20px', position: 'relative',
        }}>
          <div style={{
            position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)',
            fontSize: 48, opacity: 0.2,
          }}>{TIER_ICONS[currentCode] || '🎫'}</div>

          <div style={{ fontSize: 12, color: 'rgba(255,255,255,.5)', letterSpacing: 2, marginBottom: 6 }}>当前会员</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#fff', fontFamily: "'Georgia', serif" }}>
            {tiers.find(t => t.code === currentCode)?.name || '普通会员'}
          </div>
          {myMembership?.tier_end_date && !isExpired && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'rgba(255,255,255,.6)' }}>
              有效期至：{new Date(myMembership.tier_end_date).toLocaleDateString('zh-CN')}
            </div>
          )}
          {isExpired && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#FF6B6B', fontWeight: 600 }}>
              会员已过期，请续费
            </div>
          )}
          {currentCode !== 'normal' && myMembership?.tier_info?.monthly_bid_limit > 0 && (
            <div style={{ marginTop: 6, fontSize: 12, color: 'rgba(255,255,255,.6)' }}>
              本月已用 {myMembership.month_bid_used || 0} / {myMembership.tier_info.monthly_bid_limit} 次
            </div>
          )}
          {currentCode !== 'normal' && !myMembership?.tier_info?.monthly_bid_limit && (
            <div style={{ marginTop: 6, fontSize: 12, color: 'rgba(255,255,255,.6)' }}>
              不限次数代拍
            </div>
          )}
        </div>

        {/* Tier plans */}
        <div style={{ padding: '20px 16px 0' }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: INK, marginBottom: 16 }}>选择会员方案</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {tiers.map(tier => {
              const isCurrent = tier.code === currentCode && !isExpired;
              const isHigher = (tierOrder[tier.code] ?? 0) > (tierOrder[currentCode] ?? 0);
              const isLower = (tierOrder[tier.code] ?? 0) < (tierOrder[currentCode] ?? 0) && !isExpired;
              const color = TIER_COLORS[tier.code] || MUTED;

              return (
                <div key={tier.code} style={{
                  background: '#fff', border: `1px solid ${isCurrent ? color : BD}`,
                  borderRadius: 12, padding: '18px 20px', position: 'relative',
                  boxShadow: isCurrent ? `0 0 0 2px ${color}20` : 'none',
                }}>
                  {isCurrent && (
                    <div style={{
                      position: 'absolute', top: -1, right: 16, padding: '2px 10px',
                      background: color, color: '#fff', fontSize: 10, fontWeight: 600,
                      borderRadius: '0 0 6px 6px',
                    }}>当前方案</div>
                  )}
                  {tier.code === 'institution' && !isCurrent && (
                    <div style={{
                      position: 'absolute', top: -1, right: 16, padding: '2px 10px',
                      background: RED, color: '#fff', fontSize: 10, fontWeight: 600,
                      borderRadius: '0 0 6px 6px',
                    }}>推荐</div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                    <span style={{ fontSize: 24 }}>{TIER_ICONS[tier.code] || '🎫'}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: INK }}>{tier.name}</div>
                      <div style={{ fontSize: 12, color: MUTED }}>{tier.description}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {tier.subscription_price > 0 ? (
                        <>
                          <div style={{ fontSize: 22, fontWeight: 800, color, fontFamily: 'var(--mono,monospace)' }}>
                            ¥{tier.subscription_price}
                          </div>
                          <div style={{ fontSize: 10, color: MUTED }}>
                            {tier.period_months === 1 ? '/月' : tier.period_months === 12 ? '/年' : `/${tier.period_months}月`}
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ fontSize: 22, fontWeight: 800, color, fontFamily: 'var(--mono,monospace)' }}>
                            ¥{tier.per_bid_fee}
                          </div>
                          <div style={{ fontSize: 10, color: MUTED }}>/次</div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Features */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                    {tier.monthly_bid_limit > 0 && (
                      <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 11, background: '#F0F7FF', color: '#2563EB' }}>
                        每月{tier.monthly_bid_limit}次
                      </span>
                    )}
                    {tier.monthly_bid_limit === 0 && tier.subscription_price > 0 && (
                      <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 11, background: '#FFF8E1', color: GOLD }}>
                        不限次数
                      </span>
                    )}
                    <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 11, background: '#F0FDF4', color: GREEN }}>
                      手续费 {(tier.service_fee_rate * 100).toFixed(1)}%
                    </span>
                    {tier.deposit_required ? (
                      <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 11, background: '#FFF5F5', color: RED }}>
                        保证金 {(tier.deposit_rate * 100).toFixed(0)}%
                      </span>
                    ) : (
                      <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 11, background: '#F0FDF4', color: GREEN }}>
                        免保证金
                      </span>
                    )}
                  </div>

                  {/* Action button */}
                  {isCurrent ? (
                    <div style={{ fontSize: 13, color: MUTED, textAlign: 'center', padding: '8px 0' }}>当前方案</div>
                  ) : isLower ? (
                    <div style={{ fontSize: 13, color: MUTED, textAlign: 'center', padding: '8px 0' }}>—</div>
                  ) : tier.subscription_price > 0 ? (
                    <button
                      onClick={() => handlePurchase(tier.code, tier.name, tier.subscription_price)}
                      disabled={purchasing}
                      style={{
                        width: '100%', padding: '10px 0', borderRadius: 8,
                        background: color, border: 'none', color: '#fff',
                        fontSize: 14, fontWeight: 600, cursor: purchasing ? 'not-allowed' : 'pointer',
                        opacity: purchasing ? 0.6 : 1,
                      }}
                    >
                      {isHigher && !isExpired ? '升级（可补差价）' : isExpired && tier.code === currentCode ? '续费' : '立即开通'}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>

        {/* Upgrade note */}
        <div style={{ margin: '20px 16px 0', padding: '14px 16px', background: '#fff', borderRadius: 10, border: `1px solid ${BD}` }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 10 }}>会员说明</div>
          <div style={{ fontSize: 13, color: MUTED, lineHeight: 2 }}>
            · 普通会员无需订阅，每次代拍收取 ¥200 服务费<br />
            · 月费会员每月可享 10 次免费代拍服务，超出后按普通会员标准收费<br />
            · 年费会员不限代拍次数，性价比最高<br />
            · 月费会员可随时补差价升级为年费会员，已付月费按剩余天数折抵<br />
            · 会员费从保证金账户扣除，请确保余额充足<br />
            · 会员到期后自动降为普通会员，不影响已提交的委托
          </div>
        </div>

        {/* Deposit shortcut */}
        <div style={{ margin: '16px 16px 0', display: 'flex', gap: 10 }}>
          <button onClick={() => navigate('/deposit')} style={{
            flex: 1, padding: '12px 0', borderRadius: 8, background: '#fff',
            border: `1px solid ${BD}`, cursor: 'pointer', fontSize: 13, color: INK,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
            💰 充值保证金
          </button>
          <button onClick={() => navigate('/membership/rules')} style={{
            flex: 1, padding: '12px 0', borderRadius: 8, background: '#fff',
            border: `1px solid ${BD}`, cursor: 'pointer', fontSize: 13, color: INK,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
            📖 详细规则
          </button>
        </div>
      </div>
    </div>
  );
}
